import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useApiContext, type UseApiContextOptions } from "./useApiContext";

export interface VisibleWidget {
  id: string;
  type: string;
  name: string;
  data?: Record<string, any>;
}

export interface OsContextWindow {
  appId: string;
  title: string;
  isFocused: boolean;
  isMinimized: boolean;
  params?: Record<string, string>;
}

export interface OsContextWorkspace {
  id: string;
  name: string;
  isActive: boolean;
  windows: OsContextWindow[];
}

export interface OsContext {
  workspaces: OsContextWorkspace[];
  focusedApp: string;
  focusedWindow: {
    appId: string;
    title: string;
    params?: Record<string, string>;
  } | null;
}

export interface RichContext {
  route: string;
  routeParams: Record<string, string>;
  selectedTab?: string;
  visibleWidgets: VisibleWidget[];
  contextDescription: string;
  apiContext?: {
    recentCalls: number;
    activeResources: Array<{ id: string; type: string; data?: any }>;
    pageData: Record<string, any>;
    summary: string;
  };
  urlInferredContext?: {
    resourceType: string;
    resourceId?: string;
    resourceName?: string;
  };
  // OS layout context (only present in OS mode)
  osContext?: OsContext;
  // App state data (set via setApiData from AniContext)
  appState?: any;
}

/**
 * Infer resource context from URL when API context is not available
 */
function inferContextFromUrl(pathname: string, params: Record<string, any>): {
  resourceType: string;
  resourceId?: string;
  resourceName?: string;
} | undefined {
  const urlPatterns = [
    // Report Builder
    { regex: /^\/report-builder\/([^/?]+)/, type: "report", idIndex: 1 },
    { regex: /^\/report-builder/, type: "report", id: "list" },
    
    // Portfolios
    { regex: /^\/portfolio\/([^/?]+)/, type: "portfolio", idIndex: 1 },
    { regex: /^\/portfolios/, type: "portfolio", id: "list" },
    
    // Scenario Tracker
    { regex: /^\/scenario-tracker\/([^/?]+)/, type: "scenario", idIndex: 1 },
    { regex: /^\/scenario-tracker/, type: "scenario", id: "list" },
    
    // Workers
    { regex: /^\/workers\/([^/?]+)/, type: "worker", idIndex: 1 },
    { regex: /^\/workers/, type: "worker", id: "list" },
    { regex: /^\/worker/, type: "worker", id: "overview" },
    
    // Screener
    { regex: /^\/screener/, type: "screener", id: "search" },
    
    // Dashboard
    { regex: /^\/dashboard/, type: "dashboard", id: "main" },
    
    // Documents
    { regex: /^\/documents\/([^/?]+)/, type: "document", idIndex: 1 },
    { regex: /^\/documents/, type: "document", id: "list" },
    
    // Cash Flow
    { regex: /^\/cash-flow/, type: "cash-flow", id: "analysis" },
    
    // Playground
    { regex: /^\/playground/, type: "playground", id: "analysis" },
    
    // Pods
    { regex: /^\/pod\/([^/?]+)/, type: "pod", idIndex: 1 },
    { regex: /^\/pods/, type: "pod", id: "list" },
    
    // Products
    { regex: /^\/products\/([^/?]+)/, type: "product", idIndex: 1 },
    
    // Generic page inference
    { regex: /^\/([^/?]+)/, type: "page", idIndex: 1 },
  ];

  for (const pattern of urlPatterns) {
    const match = pathname.match(pattern.regex);
    if (match) {
      const resourceId = pattern.idIndex 
        ? match[pattern.idIndex] 
        : ('id' in pattern ? pattern.id : undefined);
      
      // Try to get a friendly name from URL params
      const resourceName = params.name || params.title;
      
      return {
        resourceType: pattern.type,
        resourceId,
        resourceName,
      };
    }
  }

  return undefined;
}

export function useRichAniContext(options: UseApiContextOptions = {}) {
  const location = useLocation();
  const params = useParams();
  const [visibleWidgets, setVisibleWidgets] = useState<VisibleWidget[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Get API context data
  const apiContext = useApiContext(options);

  // Extract tab from URL search params
  const selectedTab = new URLSearchParams(location.search).get("tab") || undefined;

  const observedWidgetsRef = useRef<WeakSet<Element>>(new WeakSet());

  // Track AniTrackableWidget elements (mounted dynamically in OS windows and on route change).
  useEffect(() => {
    const intersection = new IntersectionObserver(
      (entries) => {
        setVisibleWidgets((prevVisible) => {
          const visibleMap = new Map(prevVisible.map((widget) => [widget.id, widget]));

          entries.forEach((entry) => {
            const element = entry.target as HTMLElement;
            const widgetData = element.dataset;

            if (widgetData.aniWidget && widgetData.aniWidgetId) {
              if (entry.isIntersecting) {
                visibleMap.set(widgetData.aniWidgetId, {
                  id: widgetData.aniWidgetId,
                  type: widgetData.aniWidgetType || "unknown",
                  name: widgetData.aniWidgetName || "Widget",
                  data: widgetData.aniWidgetData
                    ? JSON.parse(widgetData.aniWidgetData)
                    : undefined,
                });
              } else {
                visibleMap.delete(widgetData.aniWidgetId);
              }
            }
          });

          return Array.from(visibleMap.values());
        });
      },
      { threshold: 0.3, rootMargin: "0px" },
    );
    observerRef.current = intersection;

    const observeNewWidgets = () => {
      document.querySelectorAll("[data-ani-widget]").forEach((widget) => {
        if (!observedWidgetsRef.current.has(widget)) {
          observedWidgetsRef.current.add(widget);
          intersection.observe(widget);
        }
      });
    };

    observeNewWidgets();

    const mutation = new MutationObserver(() => {
      observeNewWidgets();
    });
    mutation.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutation.disconnect();
      intersection.disconnect();
      observerRef.current = null;
    };
  }, [location.pathname]);

  // Generate context description
  const generateContextDescription = useCallback((): string => {
    const parts: string[] = [];
    
    // Add route context
    const routePath = location.pathname.split("/")[1];
    parts.push(routePath || "home");

    // Add specific resource (e.g., portfolio ID)
    if (params.id) {
      parts.push(`ID: ${params.id}`);
    }

    // Add tab if selected
    if (selectedTab) {
      parts.push(`${selectedTab} tab`);
    }

    // Add API context if available
    if (apiContext.activeResources.length > 0) {
      const resourceSummary = apiContext.activeResources
        .slice(0, 2)
        .map((r) => `${r.type}:${r.id}`)
        .join(", ");
      parts.push(resourceSummary);
    } else {
      // Fallback to URL-inferred context if no API context
      const urlContext = inferContextFromUrl(location.pathname, params);
      if (urlContext && urlContext.resourceId && urlContext.resourceId !== "list") {
        parts.push(`${urlContext.resourceType}:${urlContext.resourceId}`);
      }
    }

    // Add visible widgets
    if (visibleWidgets.length > 0) {
      const widgetNames = visibleWidgets.map((w) => w.name).slice(0, 3);
      parts.push(`viewing: ${widgetNames.join(", ")}`);
      
      if (visibleWidgets.length > 3) {
        parts.push(`+${visibleWidgets.length - 3} more`);
      }
    }

    return parts.join(" • ");
  }, [location.pathname, params, selectedTab, visibleWidgets, apiContext.activeResources]);

  // Get URL-inferred context (used as fallback when no API resources)
  const urlInferredContext = apiContext.activeResources.length === 0 
    ? inferContextFromUrl(location.pathname, params)
    : undefined;

  const richContext: RichContext = {
    route: location.pathname,
    routeParams: params as Record<string, string>,
    selectedTab,
    visibleWidgets,
    contextDescription: generateContextDescription(),
    apiContext: {
      recentCalls: apiContext.recentContext.length,
      activeResources: apiContext.activeResources,
      pageData: apiContext.pageData,
      summary: apiContext.contextSummary,
    },
    urlInferredContext,
  };

  return richContext;
}
